import { UploadedFile } from '@/shared/models/uploaded-file';

import { IDocUploaderForm } from './types';

const createUploadForm = <TDocType extends string>(
  docType: TDocType,
  upload: UploadedFile<TDocType> | undefined,
): IDocUploaderForm<TDocType> => {
  if (upload === undefined) {
    return { docType };
  }

  return {
    docType,
    file: {
      type: upload.docFormat === 'pdf' || upload.docFormat === 'sig' ? upload.docFormat : 'image',
      url: upload.previewUrl || upload.downloadUrl,
    },
    rejectReason: upload.rejectReason,
    status: upload.status,
    expiresAt: upload.expiresAt,
  };
};

const applyUploadFormChange = <TDocType, K extends keyof IDocUploaderForm<TDocType>>(
  form: IDocUploaderForm<TDocType>,
  name: K,
  value: IDocUploaderForm<TDocType>[K],
): IDocUploaderForm<TDocType> => ({
  ...form,
  [name]: value,
  ...(name === 'file' ? { status: undefined, rejectReason: undefined } : {}),
});

const isNewFile = <TDocType>(form: IDocUploaderForm<TDocType>) => form.file?.file instanceof File;

// an accepted upload keeps its File but gets a status, so only a status-less new file is unsent
const hasUnsentFile = <TDocType>(form: IDocUploaderForm<TDocType>) =>
  isNewFile(form) && form.status === undefined;

const needsNewFile = <TDocType>(form: IDocUploaderForm<TDocType>) =>
  form.status === 'rejected' || form.status === 'expired';

// expiresAt reaches the API only together with a file
const isExpiresLocked = <TDocType>(form: IDocUploaderForm<TDocType>) =>
  Boolean(form.file) && !hasUnsentFile(form);

export {
  createUploadForm,
  applyUploadFormChange,
  isNewFile,
  hasUnsentFile,
  needsNewFile,
  isExpiresLocked,
};
